import { Box } from '@ff-it/ui';
import { timestamp } from 'components/columns';
import { Period, TruncatedList } from 'components';
import { EnterpriseLink } from 'components/EnterpriseLink';
import { BookingStateBadge, PaymentStateBadge, InvoiceSource } from '../common';
import { fmtMonth } from 'utilities';
import type { ListExpense } from './types';
import { CompanyTitle } from 'modules/core/company/components';
import { IsLinkedStateBadge } from '../common/IsLinkedStateBadge';
import { DocumentApplicationStateBadge } from '../common/DocumentApplicationStateBadge';
import { LinkApplicationStateBadge } from '../common/LinkApplicationStateBadge';
import { InvoiceStateTag } from '../common/InvoiceStateTag';
import { InvoiceTypeIcon } from '../InvoiceTypeIcon';
import { ControlledTableColumns } from 'components/ControlledTable';
import { OverflowContainer } from 'components/OverflowContainer/OverflowContainer';
import { datesColumns, numberColumnWidth, totalsColumn } from '../common/columns';

export const columns: ControlledTableColumns<ListExpense> = [
  {
    content: 'Number',
    key: 'display_number',
    required: true,
    width: numberColumnWidth,
    cellProps: { className: 'text-nowrap' },
    render: ({ id, number, type }: ListExpense) => (
      <Box display="flex" alignItems="center" gap="1">
        <InvoiceTypeIcon direction="expense" type={type} />
        <EnterpriseLink title={number} to={`/invoices/expense/${id}`}>
          {number}
        </EnterpriseLink>
      </Box>
    ),
  },
  {
    content: 'State',
    width: 40,
    minWidth: 40,
    cellProps: { textAlign: 'center' },
    key: 'state',
    sortable: false,
    render: ({ state }) => <InvoiceStateTag state={state} appearance="naked" iconOnly />,
  },
  {
    cellProps: { className: 'py-1' },
    content: 'Provider',
    key: 'partner.title',
    minWidth: 150,
    maxWidth: 0,
    sortable: true,
    render: (expense: ListExpense) => (
      <OverflowContainer>
        <Box display="flex" alignItems="center" gap="1">
          <InvoiceSource {...expense} />
          <Box style={{ minWidth: 0 }}>
            <CompanyTitle {...expense.partner} />
          </Box>
        </Box>
      </OverflowContainer>
    ),
  },
  {
    content: 'Clients',
    key: 'clients',
    cellProps: { className: 'py-1' },
    minWidth: 150,
    maxWidth: 0,
    render: ({ clients }: ListExpense) => (
      <TruncatedList
        limit={1}
        items={clients}
        getKey={(o) => o.id}
        renderItem={(o) => (
          <OverflowContainer>
            <CompanyTitle {...o} />
          </OverflowContainer>
        )}
      />
    ),
  },
  {
    cellProps: { className: 'py-0' },
    content: 'Finance per.',
    key: 'finance_period',
    render: ({ finance_period }) =>
      finance_period ? <Period date_from={finance_period.start} date_to={finance_period.end} fmt={fmtMonth} /> : null,
    width: 120,
  },
  timestamp('Created', 'created_at', { sortable: true, width: 140 }),
  datesColumns,
  totalsColumn,
  {
    width: 140,
    key: 'sums',
    configLabel: 'Statuses',
    render: (invoice: ListExpense) => (
      <Box display="flex" gap="1">
        <PaymentStateBadge state={invoice.sums.paym_state} />
        <BookingStateBadge state={invoice.state} booking_state={invoice.booking_state} />
        <IsLinkedStateBadge linked_at={invoice.linked_at} link_state={invoice.sums.link_state} />
        <LinkApplicationStateBadge direction={invoice.direction} link_state={invoice.sums.link_state} />
        <DocumentApplicationStateBadge invoice={invoice} />
      </Box>
    ),
  },
];
